import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Users, ShieldAlert, ListTree, Search, Download, Eye, Edit3, AlertTriangle, CheckCircle2, Lock, UserPlus, Copy, KeyRound } from "lucide-react";

const GROUP_LABELS = {
  operacion_diaria: "Operación diaria", citas_taller: "Citas y taller",
  atenciones_comerciales: "Atenciones", clientes_comunicacion: "Clientes",
  marketing_contenido: "Marketing", administracion_interna: "Admin",
  rrhh: "RRHH", sistema_seguridad: "Sistema y seguridad",
};

export default function AccesosPermisosPage() {
  const { axiosAuth, permissions, isSuperAdmin } = useAuth();
  // Permisos efectivos para mostrar Auditoría
  const canSeeAudit = isSuperAdmin || (permissions?.auditoria_permisos || []).includes("ver");
  const canExportAudit = isSuperAdmin || (permissions?.auditoria_permisos || []).includes("exportar");

  const initialTab = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const t = sp.get("tab");
      const allowed = ["usuarios", "roles", "matriz"];
      if (canSeeAudit) allowed.push("auditoria");
      return allowed.includes(t) ? t : "matriz";
    } catch { return "matriz"; }
  })();
  const [tab, setTab] = useState(initialTab);

  return (
    <div className="p-6 space-y-4" data-testid="accesos-permisos-page">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Accesos y permisos</h1>
        <p className="text-xs text-slate-500">
          Fuente única de verdad de módulos, roles y permisos. Reemplaza las antiguas pantallas legacy.
        </p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 rounded-sm">
          <TabsTrigger value="usuarios" className="text-xs uppercase tracking-wider rounded-sm" data-testid="tab-usuarios"><Users className="w-3 h-3 mr-1" /> Usuarios</TabsTrigger>
          <TabsTrigger value="roles" className="text-xs uppercase tracking-wider rounded-sm" data-testid="tab-roles"><ShieldCheck className="w-3 h-3 mr-1" /> Roles</TabsTrigger>
          <TabsTrigger value="matriz" className="text-xs uppercase tracking-wider rounded-sm" data-testid="tab-matriz"><ListTree className="w-3 h-3 mr-1" /> Matriz</TabsTrigger>
          {canSeeAudit && (
            <TabsTrigger value="auditoria" className="text-xs uppercase tracking-wider rounded-sm" data-testid="tab-auditoria"><ShieldAlert className="w-3 h-3 mr-1" /> Auditoría</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="usuarios" className="mt-4"><UsersTab axiosAuth={axiosAuth} /></TabsContent>
        <TabsContent value="roles" className="mt-4"><RolesTab axiosAuth={axiosAuth} /></TabsContent>
        <TabsContent value="matriz" className="mt-4"><MatrixTab axiosAuth={axiosAuth} /></TabsContent>
        {canSeeAudit && (
          <TabsContent value="auditoria" className="mt-4"><AuditTab axiosAuth={axiosAuth} canExport={canExportAudit} /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ─── USUARIOS TAB ─────────────────────────────────────────────
function UsersTab({ axiosAuth }) {
  const { permissions, isSuperAdmin } = useAuth();
  const canCreateUsers = isSuperAdmin
    || (permissions?.accesos_permisos || []).includes("configurar")
    || (permissions?.usuarios || []).includes("crear");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selUser, setSelUser] = useState(null);
  const [eff, setEff] = useState(null);
  const [simRole, setSimRole] = useState("");
  const [simResult, setSimResult] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    return axiosAuth().get("/system/users-overview")
      .then((r) => setUsers(r.data?.users || []))
      .catch(() => toast.error("Error cargando usuarios"))
      .finally(() => setLoading(false));
  }, [axiosAuth]);

  useEffect(() => {
    const t = setTimeout(() => { reload(); }, 0);
    return () => clearTimeout(t);
  }, [reload]);

  const filtered = users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q) || (u.role || "").toLowerCase().includes(q);
  });

  const openEffective = async (u) => {
    setSelUser(u); setEff(null);
    try {
      const r = await axiosAuth().get(`/system/users/${u.id}/effective-permissions`);
      setEff(r.data);
    } catch (e) { toast.error("Error cargando permisos efectivos"); }
  };

  const simulate = async () => {
    try {
      const body = selUser ? { user_id: selUser.id } : { role: simRole };
      const r = await axiosAuth().post("/system/permissions-simulate", body);
      setSimResult(r.data);
    } catch { toast.error("Error simulando accesos"); }
  };

  return (
    <div className="space-y-3" data-testid="users-tab">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Buscar por nombre / email / rol</label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 text-xs rounded-sm" data-testid="users-search" />
        </div>
        <div className="flex items-end gap-2">
          {canCreateUsers && (
            <Button onClick={() => setShowCreate(true)} className="h-8 text-xs rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="users-new-btn"><UserPlus className="w-3 h-3 mr-1" /> Nuevo usuario</Button>
          )}
          <Select value={simRole} onValueChange={setSimRole}>
            <SelectTrigger className="h-8 text-xs rounded-sm w-48" data-testid="simulate-role-select"><SelectValue placeholder="Simular rol…" /></SelectTrigger>
            <SelectContent>
              {["super_admin", "admin", "rrhh", "jefe_taller", "jefe_recambios", "recepcion", "empleado"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => { if (simRole) { setSelUser(null); simulate(); } else { toast.warning("Elige un rol o haz click en un usuario"); } }} className="h-8 text-xs rounded-sm" variant="outline" data-testid="simulate-btn"><Eye className="w-3 h-3 mr-1" /> Simular accesos</Button>
        </div>
        <div className="text-[11px] text-slate-500">{filtered.length} / {users.length} usuarios</div>
      </div>

      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs" data-testid="users-table">
            <thead className="bg-slate-50 border-b border-slate-200"><tr>
              <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-slate-600">Nombre</th>
              <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-slate-600">Email</th>
              <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-slate-600">Rol</th>
              <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-slate-600">Centro</th>
              <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-slate-600">Depto</th>
              <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-slate-600">Empleado</th>
              <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-slate-600">Estado</th>
              <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-slate-600">Últ. acceso</th>
              <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-slate-600">Acciones</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="px-3 py-4 text-center text-slate-400">Cargando…</td></tr>}
              {!loading && filtered.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2 font-semibold">{u.name || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{u.email}</td>
                  <td className="px-3 py-2"><code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded-sm">{u.role}</code></td>
                  <td className="px-3 py-2 text-slate-600">{u.employee?.center || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{u.employee?.department || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{u.employee?.name || (u.employee_id ? <span className="text-amber-700 italic">(huérfano)</span> : "—")}</td>
                  <td className="px-3 py-2">{u.active ? <span className="text-emerald-700">Activo</span> : <span className="text-red-600">Inactivo</span>}{u.has_overrides && <span className="ml-1 text-[9px] bg-purple-100 text-purple-700 px-1 py-0.5 rounded-sm">+ overrides</span>}</td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">{u.last_login_at ? new Date(u.last_login_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm" onClick={() => openEffective(u)} data-testid={`user-effective-${u.id}`}><Eye className="w-3 h-3 mr-1" /> Permisos</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Modal permisos efectivos */}
      <Dialog open={!!selUser && !simResult} onOpenChange={(o) => { if (!o) { setSelUser(null); setEff(null); } }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="effective-perms-dialog">
          <DialogHeader>
            <DialogTitle>Permisos efectivos · {selUser?.name || selUser?.email}</DialogTitle>
            <DialogDescription>Combinación de rol base + overrides personales. <code className="text-[10px]">user_id: {selUser?.id}</code> · <code className="text-[10px]">employee_id: {selUser?.employee_id || "—"}</code></DialogDescription>
          </DialogHeader>
          {!eff && <p className="text-xs text-slate-500">Cargando…</p>}
          {eff && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div><strong>Rol base:</strong> <code>{eff.user.role}</code></div>
                <div><strong>Super admin:</strong> {eff.is_super_admin ? "SÍ" : "no"}</div>
                <div className="col-span-2"><strong>Overrides personales:</strong> {Object.keys(eff.user_overrides?.grants || {}).length + Object.keys(eff.user_overrides?.revokes || {}).length || 0}</div>
              </div>
              <div>
                <h4 className="font-bold mb-1">Módulos visibles ({eff.visible_modules.length})</h4>
                <div className="space-y-1 max-h-72 overflow-y-auto border border-slate-200 rounded-sm p-2">
                  {eff.visible_modules.map((m) => (
                    <div key={m.key} className="flex items-center justify-between gap-2 text-[11px] border-b border-slate-100 last:border-0 py-1">
                      <span><strong>{m.label}</strong> <code className="text-slate-400">({m.key})</code></span>
                      <span className="font-mono text-[10px]">{m.actions.join(", ") || "ver"}</span>
                    </div>
                  ))}
                </div>
              </div>
              {!eff.is_super_admin && (
                <CustomPermsSection
                  axiosAuth={axiosAuth}
                  user={eff.user}
                  initial={eff.user_overrides || { grants: {}, revokes: {} }}
                  onSaved={() => openEffective(selUser)}
                />
              )}
              <Button size="sm" variant="outline" className="text-xs rounded-sm" onClick={simulate} data-testid="user-simulate-btn"><Eye className="w-3 h-3 mr-1" /> Simular sidebar como este usuario</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal simulación */}
      <Dialog open={!!simResult} onOpenChange={(o) => { if (!o) { setSimResult(null); setSelUser(null); } }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="simulate-dialog">
          <DialogHeader>
            <DialogTitle>Simulación de accesos {simResult?.is_super_admin && <span className="text-emerald-600 text-xs">[super_admin]</span>}</DialogTitle>
            <DialogDescription>Cómo vería el menú: {simResult?.simulated_for?.email || simResult?.simulated_for?.role}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 text-xs max-h-96 overflow-y-auto">
            {(simResult?.modules || []).map((m) => (
              <div key={m.key} className={`flex items-center justify-between p-2 rounded-sm border ${m.can_see ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200 opacity-60"}`} data-testid={`sim-module-${m.key}`}>
                <span className="flex items-center gap-2">
                  {m.can_see ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Lock className="w-3 h-3 text-slate-400" />}
                  <strong>{m.label}</strong> <code className="text-[10px] text-slate-400">({m.key})</code>
                </span>
                {!m.can_see && <span className="text-[10px] text-red-600 italic">{m.blocked_reason}</span>}
                {m.can_see && <span className="text-[10px] font-mono">{(m.effective_actions || []).join(",") || "ver"}</span>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal crear usuario (Iter 196) */}
      {showCreate && (
        <CreateUserDialog
          axiosAuth={axiosAuth}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); reload(); }}
        />
      )}

    </div>
  );
}

// ─── ROLES TAB ────────────────────────────────────────────────
function RolesTab({ axiosAuth }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [confirmStep, setConfirmStep] = useState(0); // 0=editing, 1=preview, 2=final
  const [previewResult, setPreviewResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axiosAuth().get("/system/permissions-matrix").then((r) => setData(r.data)).catch(() => toast.error("Error matriz"));
  }, [axiosAuth]);

  const openEditor = (role) => {
    setSelectedRole(role);
    setEditPerms(JSON.parse(JSON.stringify(data?.matrix?.[role.slug] || {})));
    setConfirmStep(0);
    setPreviewResult(null);
  };

  const toggleAction = (modKey, action) => {
    setEditPerms((p) => {
      const cur = p[modKey] || [];
      const next = cur.includes(action) ? cur.filter((a) => a !== action) : [...cur, action];
      return { ...p, [modKey]: next };
    });
  };

  const submit = async (confirmed) => {
    setSaving(true);
    try {
      const r = await axiosAuth().put(`/system/roles/${selectedRole.slug}/permissions`, {
        permissions: editPerms, confirmed_impact: confirmed,
      });
      if (r.data?.preview) {
        setPreviewResult(r.data); setConfirmStep(1);
      } else {
        toast.success("Permisos del rol actualizados");
        setSelectedRole(null); setConfirmStep(0); setPreviewResult(null);
        // Reload
        const r2 = await axiosAuth().get("/system/permissions-matrix");
        setData(r2.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  if (!data) return <Card><CardContent className="p-6 text-sm text-slate-500">Cargando…</CardContent></Card>;

  return (
    <div className="space-y-3" data-testid="roles-tab">
      <Card className="rounded-sm border-slate-200"><CardContent className="p-0">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b"><tr>
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-slate-600">Rol</th>
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-slate-600">Permisos asignados</th>
            <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-slate-600">Acción</th>
          </tr></thead>
          <tbody>
            {data.roles.map((r) => {
              const isSuper = r.is_super_admin;
              const total = isSuper ? "TODOS" : Object.keys(data.matrix?.[r.slug] || {}).length;
              return (
                <tr key={r.slug} className="border-b border-slate-100" data-testid={`role-row-${r.slug}`}>
                  <td className="px-3 py-2 font-semibold"><code>{r.slug}</code> {isSuper && <span className="text-[9px] uppercase font-bold text-red-700 bg-red-50 px-1 rounded-sm border border-red-200 ml-1">protegido</span>}</td>
                  <td className="px-3 py-2 text-slate-600">{total} módulos</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" disabled={isSuper} onClick={() => openEditor(r)} data-testid={`role-edit-${r.slug}`} className="h-7 text-[11px] rounded-sm">
                      <Edit3 className="w-3 h-3 mr-1" /> {isSuper ? "Bloqueado" : "Editar"}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent></Card>

      {/* Editor de rol */}
      <Dialog open={!!selectedRole && confirmStep === 0} onOpenChange={(o) => { if (!o) { setSelectedRole(null); setConfirmStep(0); } }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="role-editor-dialog">
          <DialogHeader>
            <DialogTitle>Editar permisos · {selectedRole?.slug}</DialogTitle>
            <DialogDescription>Marca las acciones permitidas en cada módulo. Al guardar, se mostrará el impacto antes de confirmar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            <div className="overflow-x-auto max-h-[55vh]">
              <table className="w-full" data-testid="role-editor-table">
                <thead className="bg-slate-50 border-b sticky top-0"><tr>
                  <th className="text-left px-2 py-1.5">Módulo</th>
                  {data.actions.filter((a) => a !== "todo").map((a) => <th key={a} className="text-center px-1 py-1.5 text-[10px] uppercase">{a}</th>)}
                </tr></thead>
                <tbody>
                  {data.modules.filter((m) => !m.hidden_in_menu).map((m) => (
                    <tr key={m.key} className="border-b border-slate-100" data-testid={`role-editor-row-${m.key}`}>
                      <td className="px-2 py-1.5"><strong>{m.label}</strong> <code className="text-[9px] text-slate-400">({m.key})</code></td>
                      {data.actions.filter((a) => a !== "todo").map((a) => (
                        <td key={a} className="text-center px-1 py-1.5">
                          <Checkbox
                            checked={(editPerms[m.key] || []).includes(a)}
                            onCheckedChange={() => toggleAction(m.key, a)}
                            disabled={!(m.available_actions || []).includes(a)}
                            data-testid={`role-cb-${m.key}-${a}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setSelectedRole(null); setConfirmStep(0); }} data-testid="role-cancel-btn">Cancelar</Button>
            <Button onClick={() => submit(false)} disabled={saving} data-testid="role-preview-btn"><AlertTriangle className="w-3 h-3 mr-1" /> Ver impacto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Doble confirmación */}
      <Dialog open={confirmStep === 1 && !!previewResult} onOpenChange={(o) => { if (!o) setConfirmStep(0); }}>
        <DialogContent className="max-w-2xl" data-testid="role-impact-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Confirmar cambio de permisos</DialogTitle>
            <DialogDescription>
              <strong className="text-red-700">Este cambio afectará a {previewResult?.impacted_users} usuario(s)</strong> con el rol <code>{selectedRole?.slug}</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs space-y-2">
            {previewResult?.diff?.added && Object.keys(previewResult.diff.added).length > 0 && (
              <div><strong className="text-emerald-700">Añadidos:</strong> <code className="text-[10px]">{JSON.stringify(previewResult.diff.added)}</code></div>
            )}
            {previewResult?.diff?.removed && Object.keys(previewResult.diff.removed).length > 0 && (
              <div><strong className="text-red-700">Eliminados:</strong> <code className="text-[10px]">{JSON.stringify(previewResult.diff.removed)}</code></div>
            )}
            {previewResult?.diff?.changed && Object.keys(previewResult.diff.changed).length > 0 && (
              <div><strong className="text-amber-700">Modificados:</strong> <code className="text-[10px]">{JSON.stringify(previewResult.diff.changed)}</code></div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmStep(0)} data-testid="role-confirm-cancel">Volver a editar</Button>
            <Button onClick={() => submit(true)} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="role-confirm-save"><CheckCircle2 className="w-3 h-3 mr-1" /> Confirmar y guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── MATRIX TAB (con export CSV) ──────────────────────────────
function MatrixTab({ axiosAuth }) {
  const [data, setData] = useState(null);
  const [groupFilter, setGroupFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    axiosAuth().get("/system/permissions-matrix").then((r) => setData(r.data)).catch((e) => toast.error("Error matriz"));
  }, [axiosAuth]);

  const filteredModules = useMemo(() => {
    if (!data?.modules) return [];
    return data.modules.filter((m) => {
      if (groupFilter !== "all" && m.menu_group !== groupFilter) return false;
      if (search && !(m.label.toLowerCase().includes(search.toLowerCase()) || m.key.includes(search.toLowerCase()))) return false;
      return true;
    });
  }, [data, groupFilter, search]);

  const exportCSV = () => {
    if (!data) return;
    const headers = ["module_key", "module_label", "menu_group", ...data.roles.map((r) => r.slug)];
    const rows = filteredModules.map((m) => {
      const row = [m.key, `"${m.label.replace(/"/g, '""')}"`, m.menu_group];
      for (const r of data.roles) {
        const acts = r.is_super_admin ? ["todo"] : (data.matrix?.[r.slug]?.[m.key] || []);
        row.push((acts || []).join("|"));
      }
      return row.join(",");
    });
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `matriz_permisos_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (!data) return <Card><CardContent className="p-6 text-sm text-slate-500">Cargando…</CardContent></Card>;

  const cellColor = (acts, isSuper) => {
    if (isSuper) return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (!acts || acts.length === 0) return "bg-slate-50 text-slate-400 border-slate-100";
    if (acts.includes("todo")) return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (acts.length >= 4) return "bg-blue-100 text-blue-700 border-blue-200";
    if (acts.length >= 2) return "bg-amber-100 text-amber-700 border-amber-200";
    return "bg-slate-100 text-slate-700 border-slate-200";
  };
  const cellLabel = (acts, isSuper) => {
    if (isSuper) return "TODO";
    if (!acts || acts.length === 0) return "—";
    if (acts.includes("todo")) return "TODO";
    return acts.map((a) => a[0].toUpperCase()).join("");
  };

  return (
    <div className="space-y-3" data-testid="matrix-tab">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Grupo</label>
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger className="h-8 text-xs rounded-sm w-56" data-testid="matrix-group-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los grupos</SelectItem>
              {(data.menu_groups || []).map((g) => <SelectItem key={g} value={g}>{GROUP_LABELS[g] || g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Buscar módulo</label>
          <div className="relative"><Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="nombre o key…" className="h-8 text-xs rounded-sm pl-7" data-testid="matrix-search" />
          </div>
        </div>
        <Button onClick={exportCSV} variant="outline" className="h-8 text-xs rounded-sm" data-testid="matrix-export-csv"><Download className="w-3 h-3 mr-1" /> Exportar CSV</Button>
        <div className="text-[11px] text-slate-500">{filteredModules.length} / {data.modules.length} módulos · {data.roles.length} roles</div>
      </div>
      <Card className="rounded-sm border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs" data-testid="matrix-table">
          <thead className="bg-slate-50 border-b sticky top-0"><tr>
            <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-slate-600 w-72">Módulo</th>
            <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-slate-600 w-28">Grupo</th>
            {data.roles.map((r) => <th key={r.slug} className="text-center px-2 py-2 font-bold uppercase tracking-wider text-slate-600 w-24">{r.slug}</th>)}
          </tr></thead>
          <tbody>
            {filteredModules.map((m) => (
              <tr key={m.key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`matrix-row-${m.key}`}>
                <td className="px-3 py-2"><div className="font-semibold">{m.label}</div><code className="text-[10px] text-slate-400">{m.key}</code>{m.legacy && <span className="ml-1 text-[9px] uppercase font-bold text-amber-700 bg-amber-50 px-1 rounded-sm border border-amber-200">LEGACY</span>}</td>
                <td className="px-2 py-2 text-[10px] text-slate-500">{GROUP_LABELS[m.menu_group] || m.menu_group}</td>
                {data.roles.map((r) => {
                  const acts = data.matrix?.[r.slug]?.[m.key] || [];
                  return <td key={r.slug} className="px-2 py-2 text-center"><span className={`inline-flex items-center justify-center min-w-[40px] px-2 py-0.5 rounded-sm text-[10px] font-bold border ${cellColor(acts, r.is_super_admin)}`} data-testid={`matrix-cell-${m.key}-${r.slug}`}>{cellLabel(acts, r.is_super_admin)}</span></td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}


// ─── PERMISOS PERSONALIZADOS (sección dentro del diálogo del usuario) ───
function CustomPermsSection({ axiosAuth, user, initial, onSaved }) {
  const [modules, setModules] = useState([]);
  const [grants, setGrants] = useState(initial.grants || {});
  const [revokes, setRevokes] = useState(initial.revokes || {});
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const [selModKey, setSelModKey] = useState("");
  const [selAction, setSelAction] = useState("ver");
  const [selMode, setSelMode] = useState("grant"); // grant | revoke

  useEffect(() => {
    axiosAuth().get("/system/modules").then((r) => setModules(r.data?.modules || [])).catch(() => {});
  }, [axiosAuth]);

  const allActions = ["ver", "crear", "editar", "eliminar", "aprobar", "exportar", "configurar"];

  const addOverride = () => {
    if (!selModKey) { toast.warning("Elige un módulo"); return; }
    const dst = selMode === "grant" ? grants : revokes;
    const setDst = selMode === "grant" ? setGrants : setRevokes;
    const cur = new Set(dst[selModKey] || []);
    cur.add(selAction);
    setDst({ ...dst, [selModKey]: Array.from(cur) });
    setShowSelector(false);
  };

  const removeOverride = (bucket, mod, act) => {
    const dst = bucket === "grants" ? grants : revokes;
    const setDst = bucket === "grants" ? setGrants : setRevokes;
    const cur = (dst[mod] || []).filter((a) => a !== act);
    const next = { ...dst };
    if (cur.length) next[mod] = cur; else delete next[mod];
    setDst(next);
  };

  const requestPreview = async () => {
    try {
      const r = await axiosAuth().post(`/admin/users/${user.id}/permission-overrides/preview`, { grants, revokes });
      setPreview(r.data);
      setConfirmOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Error calculando impacto");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { grants, revokes, reason };
      if (preview?.requires_confirm) body.confirm = "CONFIRMO_CAMBIOS_PERMISOS_USUARIO";
      const r = await axiosAuth().put(`/admin/users/${user.id}/permission-overrides`, body);
      toast.success(`Permisos personalizados guardados (${r.data?.audit?.written || 0} cambios registrados)`);
      setConfirmOpen(false); setPreview(null);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const resetAll = async () => {
    if (!window.confirm("¿Quitar TODOS los permisos personalizados y dejar solo los del rol?")) return;
    try {
      await axiosAuth().post(`/admin/users/${user.id}/permissions/reset`, { reason: reason || "Reset desde panel" });
      toast.success("Permisos personalizados restablecidos al rol base");
      setGrants({}); setRevokes({});
      if (onSaved) onSaved();
    } catch (e) { toast.error("Error al resetear"); }
  };

  const grantsCount = Object.values(grants).flat().length;
  const revokesCount = Object.values(revokes).flat().length;
  const moduleLabel = (k) => modules.find((m) => m.key === k)?.label || k;
  const dirty = JSON.stringify({ g: grants, r: revokes }) !== JSON.stringify({ g: initial.grants || {}, r: initial.revokes || {} });

  return (
    <div className="border border-purple-200 bg-purple-50/30 rounded-sm p-3 space-y-2" data-testid="custom-perms-section">
      <div className="flex items-center justify-between">
        <h4 className="font-bold text-purple-800 text-xs uppercase tracking-wider flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Permisos personalizados</h4>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm" onClick={() => { setSelMode("grant"); setShowSelector(true); }} data-testid="add-custom-grant"><CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" /> Conceder</Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm" onClick={() => { setSelMode("revoke"); setShowSelector(true); }} data-testid="add-custom-revoke"><Lock className="w-3 h-3 mr-1 text-red-600" /> Restringir</Button>
        </div>
      </div>
      <p className="text-[10px] text-slate-600 italic">Excepciones aplicadas SOLO a este usuario sobre los permisos del rol <code>{user.role}</code>.</p>

      <div className="space-y-1">
        {grantsCount === 0 && revokesCount === 0 && <p className="text-[11px] text-slate-500 italic">No hay permisos personalizados.</p>}
        {Object.entries(grants).map(([mod, acts]) => acts.map((a) => (
          <div key={`g-${mod}-${a}`} className="flex items-center justify-between text-[11px] bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1" data-testid={`custom-grant-${mod}-${a}`}>
            <span><span className="text-[9px] uppercase font-bold text-emerald-700 mr-1">Concedido</span><strong>{moduleLabel(mod)}</strong> · <code>{a}</code></span>
            <Button size="sm" variant="ghost" className="h-5 px-1 text-[10px] text-red-600" onClick={() => removeOverride("grants", mod, a)} data-testid={`rm-grant-${mod}-${a}`}>Quitar</Button>
          </div>
        )))}
        {Object.entries(revokes).map(([mod, acts]) => acts.map((a) => (
          <div key={`r-${mod}-${a}`} className="flex items-center justify-between text-[11px] bg-red-50 border border-red-200 rounded-sm px-2 py-1" data-testid={`custom-revoke-${mod}-${a}`}>
            <span><span className="text-[9px] uppercase font-bold text-red-700 mr-1">Restringido</span><strong>{moduleLabel(mod)}</strong> · <code>{a}</code></span>
            <Button size="sm" variant="ghost" className="h-5 px-1 text-[10px] text-red-600" onClick={() => removeOverride("revokes", mod, a)} data-testid={`rm-revoke-${mod}-${a}`}>Quitar</Button>
          </div>
        )))}
      </div>

      <div className="grid grid-cols-2 gap-2 pt-1">
        <Input placeholder="Motivo del cambio (opcional)" value={reason} onChange={(e) => setReason(e.target.value)} className="h-7 text-[11px] rounded-sm" data-testid="custom-perms-reason" />
        <div className="flex gap-1 justify-end">
          {(grantsCount + revokesCount) > 0 && <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm" onClick={resetAll} data-testid="custom-perms-reset">Reset</Button>}
          <Button size="sm" disabled={!dirty || saving} onClick={requestPreview} className="h-7 text-[11px] rounded-sm bg-purple-700 hover:bg-purple-800 text-white" data-testid="custom-perms-save">Guardar cambios</Button>
        </div>
      </div>

      {/* Modal selector de override */}
      <Dialog open={showSelector} onOpenChange={setShowSelector}>
        <DialogContent className="max-w-md" data-testid="custom-perm-selector-dialog">
          <DialogHeader>
            <DialogTitle>{selMode === "grant" ? "Conceder permiso adicional" : "Restringir permiso del rol"}</DialogTitle>
            <DialogDescription>Este cambio solo afecta a <strong>{user.name || user.email}</strong>, no al resto del rol <code>{user.role}</code>.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Módulo</label>
            <Select value={selModKey} onValueChange={setSelModKey}>
              <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="custom-mod-select"><SelectValue placeholder="Elige módulo…" /></SelectTrigger>
              <SelectContent className="max-h-80">
                {modules.filter((m) => !m.hidden_in_menu).map((m) => (
                  <SelectItem key={m.key} value={m.key}>{m.label} <span className="text-[10px] text-slate-400">({m.key})</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Acción</label>
            <Select value={selAction} onValueChange={setSelAction}>
              <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="custom-action-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {allActions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSelector(false)} data-testid="custom-perm-cancel">Cancelar</Button>
            <Button onClick={addOverride} className={selMode === "grant" ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-red-700 hover:bg-red-800 text-white"} data-testid="custom-perm-confirm">{selMode === "grant" ? "Conceder" : "Restringir"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal doble confirmación con impacto */}
      <Dialog open={confirmOpen && !!preview} onOpenChange={(o) => { if (!o) { setConfirmOpen(false); setPreview(null); } }}>
        <DialogContent className="max-w-xl" data-testid="custom-perm-impact-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Confirmar cambio en permisos personalizados</DialogTitle>
            <DialogDescription>Este cambio solo afecta a <strong>{user.name || user.email}</strong>, no al resto del rol.</DialogDescription>
          </DialogHeader>
          <div className="text-xs space-y-2">
            {Object.keys(preview?.added || {}).length > 0 && (
              <div><strong className="text-emerald-700">Ganará acceso a:</strong>
                <ul className="ml-3 list-disc">
                  {Object.entries(preview?.added || {}).map(([mod, acts]) => <li key={mod}>{moduleLabel(mod)} <code className="text-[10px]">({acts.join(", ")})</code></li>)}
                </ul>
              </div>
            )}
            {Object.keys(preview?.removed || {}).length > 0 && (
              <div><strong className="text-red-700">Perderá acceso a:</strong>
                <ul className="ml-3 list-disc">
                  {Object.entries(preview?.removed || {}).map(([mod, acts]) => <li key={mod}>{moduleLabel(mod)} <code className="text-[10px]">({acts.join(", ")})</code></li>)}
                </ul>
              </div>
            )}
            {preview?.sensitive_touched?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-2">
                <strong className="text-amber-800">Tocas permisos sensibles:</strong> <code className="text-[10px]">{preview.sensitive_touched.join(", ")}</code>
              </div>
            )}
            {preview?.invalid_modules?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-2 text-red-700">Módulos inválidos: {preview.invalid_modules.join(", ")}</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="custom-impact-cancel">Volver</Button>
            <Button onClick={save} disabled={saving} className="bg-purple-700 hover:bg-purple-800 text-white" data-testid="custom-impact-save"><CheckCircle2 className="w-3 h-3 mr-1" />Confirmar y guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── AUDITORÍA TAB ────────────────────────────────────────────
function AuditTab({ axiosAuth, canExport = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [facets, setFacets] = useState(null);
  const urlBatch = (() => {
    try { return new URLSearchParams(window.location.search).get("batch_id") || ""; } catch { return ""; }
  })();
  const [filters, setFilters] = useState({
    date_from: "", date_to: "", actor_email: "", affected_type: "",
    module_key: "", change_type: "", source: "", permission_action: "",
    search: "", batch_id: urlBatch,
  });
  const [detail, setDetail] = useState(null);

  const load = async (pg = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set("page", pg); params.set("page_size", pageSize);
      const r = await axiosAuth().get(`/permissions/audit?${params.toString()}`);
      setItems(r.data?.items || []); setTotal(r.data?.total || 0); setPage(pg);
    } catch (e) { toast.error("Error cargando auditoría"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    axiosAuth().get("/permissions/audit/facets").then((r) => setFacets(r.data)).catch(() => {});
    const t = setTimeout(() => load(1), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosAuth]);

  const apply = () => load(1);
  const clear = () => { setFilters({ date_from: "", date_to: "", actor_email: "", affected_type: "", module_key: "", change_type: "", source: "", permission_action: "", search: "", batch_id: "" }); setTimeout(() => load(1), 50); };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await axiosAuth().get(`/permissions/audit/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `auditoria_permisos_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch { toast.error("Error exportando CSV"); }
  };

  const openDetail = async (batchId) => {
    try {
      const r = await axiosAuth().get(`/permissions/audit/batch/${batchId}`);
      setDetail(r.data);
    } catch { toast.error("Error cargando detalle"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-3" data-testid="audit-tab">
      {filters.batch_id && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs" data-testid="audit-batch-chip">
          <ShieldAlert className="w-3 h-3 text-amber-600" />
          <span>Filtrando por <strong>batch_id</strong>: <code className="text-[10px]">{filters.batch_id}</code></span>
          <Button size="sm" variant="ghost" className="h-6 ml-auto text-[10px]" onClick={() => { setFilters({ ...filters, batch_id: "" }); setTimeout(() => load(1), 50); }} data-testid="audit-batch-chip-clear">Quitar filtro</Button>
        </div>
      )}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-3 grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Desde</label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-8 text-xs rounded-sm" data-testid="audit-date-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Hasta</label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-8 text-xs rounded-sm" data-testid="audit-date-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Actor (email)</label>
            <Input value={filters.actor_email} onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })} placeholder="parte del email…" className="h-8 text-xs rounded-sm" data-testid="audit-actor-email" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Tipo afectado</label>
            <Select value={filters.affected_type || "all"} onValueChange={(v) => setFilters({ ...filters, affected_type: v === "all" ? "" : v })}>
              <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="audit-affected-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="role">Rol</SelectItem>
                <SelectItem value="user">Usuario</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Módulo</label>
            <Select value={filters.module_key || "all"} onValueChange={(v) => setFilters({ ...filters, module_key: v === "all" ? "" : v })}>
              <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="audit-module"><SelectValue placeholder="Todos…" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">Todos</SelectItem>
                {(facets?.modules || []).map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Tipo cambio</label>
            <Select value={filters.change_type || "all"} onValueChange={(v) => setFilters({ ...filters, change_type: v === "all" ? "" : v })}>
              <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="audit-change-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="granted">Concedido</SelectItem>
                <SelectItem value="revoked">Restringido</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Origen</label>
            <Select value={filters.source || "all"} onValueChange={(v) => setFilters({ ...filters, source: v === "all" ? "" : v })}>
              <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="audit-source"><SelectValue placeholder="Todos…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {(facets?.sources || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Acción/permiso</label>
            <Select value={filters.permission_action || "all"} onValueChange={(v) => setFilters({ ...filters, permission_action: v === "all" ? "" : v })}>
              <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="audit-action"><SelectValue placeholder="Todas…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {(facets?.permission_actions || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 md:col-span-4">
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Buscar libre (actor, afectado, motivo…)</label>
            <Input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="texto a buscar" className="h-8 text-xs rounded-sm" data-testid="audit-search" onKeyDown={(e) => { if (e.key === "Enter") apply(); }} />
          </div>
          <div className="col-span-2 md:col-span-4 flex justify-end gap-2">
            <Button variant="outline" onClick={clear} className="h-8 text-xs rounded-sm" data-testid="audit-clear">Limpiar</Button>
            <Button onClick={apply} className="h-8 text-xs rounded-sm" data-testid="audit-apply"><Search className="w-3 h-3 mr-1" /> Aplicar</Button>
            {canExport && (
              <Button variant="outline" onClick={exportCSV} className="h-8 text-xs rounded-sm" data-testid="audit-export-csv"><Download className="w-3 h-3 mr-1" /> Exportar CSV</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <div className="px-3 py-2 text-[11px] text-slate-500 border-b">{loading ? "Cargando…" : `${total} cambios — página ${page}/${totalPages}`}</div>
          <table className="w-full text-xs" data-testid="audit-table">
            <thead className="bg-slate-50 border-b"><tr>
              <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-slate-600">Fecha</th>
              <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-slate-600">Actor</th>
              <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-slate-600">Tipo</th>
              <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-slate-600">Afectado</th>
              <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-slate-600">Módulo</th>
              <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-slate-600">Permiso</th>
              <th className="text-center px-2 py-2 font-bold uppercase tracking-wider text-slate-600">Antes → Después</th>
              <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-slate-600">Origen</th>
              <th className="text-right px-2 py-2 font-bold uppercase tracking-wider text-slate-600"></th>
            </tr></thead>
            <tbody>
              {!loading && items.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">Sin resultados</td></tr>}
              {items.map((it) => (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${it.id}`}>
                  <td className="px-2 py-1.5 text-[10px] whitespace-nowrap">{new Date(it.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="px-2 py-1.5"><strong className="text-[11px]">{it.actor_name || it.actor_email}</strong><br /><code className="text-[9px] text-slate-400">{it.actor_role}</code></td>
                  <td className="px-2 py-1.5"><span className={`text-[9px] uppercase font-bold px-1 rounded-sm ${it.affected_type === "role" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>{it.affected_type}</span></td>
                  <td className="px-2 py-1.5 text-[11px]">{it.affected_name || it.affected_id}</td>
                  <td className="px-2 py-1.5"><strong className="text-[11px]">{it.module_label || it.module_key}</strong><br /><code className="text-[9px] text-slate-400">{it.module_key}</code></td>
                  <td className="px-2 py-1.5"><code className="text-[10px]">{it.permission_action}</code></td>
                  <td className="px-2 py-1.5 text-center">
                    {it.change_type === "granted" ?
                      <span className="text-[10px]"><span className="text-red-500">no</span> → <strong className="text-emerald-700">sí</strong></span>
                      : <span className="text-[10px]"><strong className="text-emerald-700">sí</strong> → <span className="text-red-500">no</span></span>
                    }
                  </td>
                  <td className="px-2 py-1.5"><code className="text-[10px] bg-slate-100 px-1 rounded-sm">{it.source}</code></td>
                  <td className="px-2 py-1.5 text-right"><Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => openDetail(it.batch_id)} data-testid={`audit-detail-${it.id}`}>Detalle</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-3 py-2 text-[11px] border-t">
              <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={page === 1} onClick={() => load(page - 1)} data-testid="audit-prev">‹ Anterior</Button>
              <span>{page} / {totalPages}</span>
              <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={page >= totalPages} onClick={() => load(page + 1)} data-testid="audit-next">Siguiente ›</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detalle batch */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="audit-batch-dialog">
          <DialogHeader>
            <DialogTitle>Detalle de cambio agrupado</DialogTitle>
            <DialogDescription>
              {detail && (<><code className="text-[10px]">{detail.batch_id}</code> · {new Date(detail.created_at).toLocaleString("es-ES")}</>)}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="text-xs space-y-3">
              <div className="grid grid-cols-2 gap-2 bg-slate-50 p-2 rounded-sm">
                <div><strong>Actor:</strong> {detail.actor?.name} <code className="text-[10px]">({detail.actor?.email})</code></div>
                <div><strong>Rol del actor:</strong> <code>{detail.actor?.role}</code></div>
                <div><strong>Afectado:</strong> <span className="font-mono text-[10px]">{detail.affected?.type}</span> — {detail.affected?.name}</div>
                <div><strong>Origen:</strong> <code className="text-[10px]">{detail.source}</code></div>
                {detail.reason && <div className="col-span-2"><strong>Motivo:</strong> {detail.reason}</div>}
                <div><strong>IP:</strong> <code className="text-[10px]">{detail.ip_address || "—"}</code></div>
                <div><strong>Cambios:</strong> <span className="text-emerald-700">+{detail.total_granted}</span> / <span className="text-red-700">-{detail.total_revoked}</span></div>
              </div>
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50 border-b"><tr>
                  <th className="text-left px-2 py-1">Módulo</th>
                  <th className="text-left px-2 py-1">Permiso</th>
                  <th className="text-center px-2 py-1">Antes</th>
                  <th className="text-center px-2 py-1">Después</th>
                </tr></thead>
                <tbody>
                  {(detail.changes || []).map((c) => (
                    <tr key={c.id} className="border-b border-slate-100">
                      <td className="px-2 py-1"><strong>{c.module_label}</strong> <code className="text-[10px] text-slate-400">({c.module_key})</code></td>
                      <td className="px-2 py-1"><code>{c.permission_action}</code></td>
                      <td className="px-2 py-1 text-center">{c.old_value ? "✓" : "—"}</td>
                      <td className="px-2 py-1 text-center">{c.new_value ? "✓" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Iter 196 — Crear usuario ──────────────────────────────────
function CreateUserDialog({ axiosAuth, onClose, onCreated }) {
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", role: "empleado",
    employee_id: "", password: "",
    must_change_password: true,
    new_employee_mode: false,
    new_employee: { first_name: "", last_name: "", center_id: "", department_id: "" },
  });
  const [centers, setCenters] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPwdOnce, setShowPwdOnce] = useState(null); // { password, user }
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [rEmp, rMat, rCen, rDep] = await Promise.all([
          axiosAuth().get("/employees/available-for-link"),
          axiosAuth().get("/system/permissions-matrix"),
          axiosAuth().get("/centers"),
          axiosAuth().get("/departments"),
        ]);
        const empData = rEmp.data;
        setEmployees(Array.isArray(empData) ? empData : (empData?.employees || []));
        setCenters(rCen.data || []);
        setDepartments(rDep.data || []);
        const matrix = rMat.data?.matrix || {};
        setRoles(Object.keys(matrix).map((slug) => ({ slug, name: slug })));
      } catch { /* fallback silencioso */ }
    })();
  }, [axiosAuth]);

  const requiredLen = (form.role === "admin" || form.role === "super_admin") ? 14 : 12;

  const generatePassword = () => {
    const upper = "ABCDEFGHJKMNPQRSTUVWXYZ";
    const lower = "abcdefghijkmnpqrstuvwxyz";
    const nums = "23456789";
    const sym = "!@#$%&*?_";
    const all = upper + lower + nums + sym;
    const len = Math.max(requiredLen, 14);
    let pwd = "";
    // Asegurar al menos uno de cada
    pwd += upper[Math.floor(Math.random() * upper.length)];
    pwd += lower[Math.floor(Math.random() * lower.length)];
    pwd += nums[Math.floor(Math.random() * nums.length)];
    pwd += sym[Math.floor(Math.random() * sym.length)];
    while (pwd.length < len) {
      pwd += all[Math.floor(Math.random() * all.length)];
    }
    // Shuffle
    pwd = pwd.split("").sort(() => Math.random() - 0.5).join("");
    setForm({ ...form, password: pwd });
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Nombre obligatorio";
    if (!form.email.trim()) e.email = "Email obligatorio";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Email inválido";
    if (!form.role) e.role = "Rol obligatorio";
    if (!form.password) e.password = "Contraseña obligatoria";
    else if (form.password.length < requiredLen) e.password = `Mínimo ${requiredLen} caracteres para rol ${form.role}`;
    if (form.role !== "super_admin" && !form.employee_id && !form.new_employee_mode) {
      e.employee = "Selecciona un empleado o crea ficha rápida";
    }
    if (form.new_employee_mode) {
      if (!form.new_employee.first_name.trim() && !form.new_employee.last_name.trim()) {
        e.new_employee = "Indica al menos nombre o apellidos";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        password: form.password,
      };
      if (form.new_employee_mode) {
        body.new_employee = {
          first_name: form.new_employee.first_name.trim(),
          last_name: form.new_employee.last_name.trim(),
          center_id: form.new_employee.center_id || "",
          department_id: form.new_employee.department_id || "",
          email: form.email.trim().toLowerCase(),
          person_type: "empleado",
        };
      } else if (form.employee_id) {
        body.employee_id = form.employee_id;
      }
      const r = await axiosAuth().post("/auth/register", body);
      // Mostrar contraseña UNA sola vez (el backend NUNCA la devuelve, la guardamos del form)
      setShowPwdOnce({ password: form.password, user: r.data });
      toast.success(`Usuario ${r.data?.email} creado correctamente`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error creando usuario");
    } finally { setSaving(false); }
  };

  const copyPassword = () => {
    if (!showPwdOnce) return;
    navigator.clipboard.writeText(showPwdOnce.password).then(
      () => toast.success("Contraseña copiada"),
      () => toast.error("No se pudo copiar"),
    );
  };

  const closeAndReload = () => { setShowPwdOnce(null); onCreated(); };

  // Pantalla final: contraseña visible una sola vez
  if (showPwdOnce) {
    return (
      <Dialog open onOpenChange={(o) => { if (!o) closeAndReload(); }}>
        <DialogContent className="max-w-md" data-testid="user-created-once-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Usuario creado</DialogTitle>
            <DialogDescription>Guarda la contraseña ahora. Por seguridad NO se mostrará otra vez.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs bg-slate-50 border border-slate-200 rounded-sm p-2 space-y-1">
              <div><strong>Email:</strong> <span data-testid="created-user-email">{showPwdOnce.user?.email}</span></div>
              <div><strong>Rol:</strong> <code>{showPwdOnce.user?.role}</code></div>
              <div className="text-[10px] text-slate-400">user_id: {showPwdOnce.user?.id}</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Contraseña temporal</label>
              <div className="flex gap-1 mt-1">
                <Input value={showPwdOnce.password} readOnly className="h-8 font-mono text-xs rounded-sm" data-testid="created-temp-password" />
                <Button size="sm" variant="outline" onClick={copyPassword} className="h-8 rounded-sm" data-testid="copy-temp-password"><Copy className="w-3 h-3" /></Button>
              </div>
              <p className="text-[10px] text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> El usuario deberá cambiarla en su primer inicio de sesión.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={closeAndReload} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="created-close-btn">He guardado la contraseña, cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-emerald-600" /> Nuevo usuario</DialogTitle>
          <DialogDescription>Crea un usuario y vincúlalo a un empleado existente o crea ficha rápida.</DialogDescription>
        </DialogHeader>
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Nombre *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-8 text-xs rounded-sm" autoComplete="off" name="display-name" data-testid="new-user-name" />
              {errors.name && <p className="text-[10px] text-red-600">{errors.name}</p>}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Email *</label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-8 text-xs rounded-sm" autoComplete="off" name="display-email" data-testid="new-user-email" />
              {errors.email && <p className="text-[10px] text-red-600">{errors.email}</p>}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Rol *</label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {roles.map((r) => <SelectItem key={r.slug} value={r.slug}>{r.name || r.slug} <span className="text-[10px] text-slate-400 ml-1">({r.slug})</span></SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-0.5">Política de contraseña: mínimo <strong>{requiredLen}</strong> caracteres para este rol.</p>
          </div>

          <div className="border-t border-slate-200 pt-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600 flex items-center gap-2">
              Vinculación con empleado
              <button type="button" className="text-[10px] underline text-slate-500" onClick={() => setForm({ ...form, new_employee_mode: !form.new_employee_mode, employee_id: "" })} data-testid="toggle-new-employee">
                {form.new_employee_mode ? "Vincular existente" : "Crear ficha rápida"}
              </button>
            </label>
            {!form.new_employee_mode ? (
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className="h-8 text-xs rounded-sm mt-1" data-testid="new-user-employee"><SelectValue placeholder="Selecciona empleado…" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name || `${e.first_name||""} ${e.last_name||""}`.trim() || "(sin nombre)"}
                      {e.email && <span className="text-[10px] text-slate-400 ml-2">{e.email}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input placeholder="Nombre" value={form.new_employee.first_name} onChange={(e) => setForm({ ...form, new_employee: { ...form.new_employee, first_name: e.target.value } })} className="h-8 text-xs rounded-sm" data-testid="new-emp-first" />
                <Input placeholder="Apellidos" value={form.new_employee.last_name} onChange={(e) => setForm({ ...form, new_employee: { ...form.new_employee, last_name: e.target.value } })} className="h-8 text-xs rounded-sm" data-testid="new-emp-last" />
                <Select value={form.new_employee.center_id} onValueChange={(v) => setForm({ ...form, new_employee: { ...form.new_employee, center_id: v } })}>
                  <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="new-emp-center"><SelectValue placeholder="Centro…" /></SelectTrigger>
                  <SelectContent>{centers.map((c) => <SelectItem key={c.id || c.slug} value={c.id || c.slug}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.new_employee.department_id} onValueChange={(v) => setForm({ ...form, new_employee: { ...form.new_employee, department_id: v } })}>
                  <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="new-emp-dept"><SelectValue placeholder="Departamento…" /></SelectTrigger>
                  <SelectContent>{departments.map((d) => <SelectItem key={d.id || d.slug} value={d.id || d.slug}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {errors.employee && <p className="text-[10px] text-red-600">{errors.employee}</p>}
            {errors.new_employee && <p className="text-[10px] text-red-600">{errors.new_employee}</p>}
          </div>

          <div className="border-t border-slate-200 pt-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Contraseña temporal *</label>
            <div className="flex gap-1 mt-1">
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-8 text-xs rounded-sm font-mono" autoComplete="off" name="temp-pwd" data-testid="new-user-password" />
              <Button type="button" size="sm" variant="outline" onClick={generatePassword} className="h-8 text-[11px] rounded-sm" data-testid="generate-password-btn"><KeyRound className="w-3 h-3 mr-1" /> Generar</Button>
            </div>
            {errors.password && <p className="text-[10px] text-red-600">{errors.password}</p>}
            <div className="flex items-center gap-2 mt-2">
              <Checkbox id="must-change" checked={form.must_change_password} onCheckedChange={(v) => setForm({ ...form, must_change_password: !!v })} disabled data-testid="must-change-password" />
              <label htmlFor="must-change" className="text-[11px] text-slate-700">Forzar cambio de contraseña en primer inicio de sesión <span className="text-[10px] text-slate-400">(siempre activo)</span></label>
            </div>
          </div>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="create-user-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="create-user-submit">{saving ? "Creando…" : "Crear usuario"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

